import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  CircularProgress,
  TablePagination,
  Tooltip,
} from '@mui/material';
import { Add as AddIcon, Visibility as ViewIcon } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { evoWebhookConfigService, EvoWebhookConfig } from '../services/firebaseFunctions';
import { useAuth } from '../contexts/AuthContext';
import LoadingOverlay from '../components/LoadingOverlay';

const EvoWebhooksPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = useAuth();
  const canManage = hasPermission('webhooks_evo', 'editar');
  const [configs, setConfigs] = useState<EvoWebhookConfig[]>([]);
  const [filteredConfigs, setFilteredConfigs] = useState<EvoWebhookConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const loadRequestRef = useRef(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    notes: '',
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filter, setFilter] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Carregamento inicial (coberto pelo overlay padrão); refetch pós-ação não usa overlay.
  const loadConfigs = useCallback(async () => {
    const requestId = ++loadRequestRef.current;
    setLoading(true);
    setLoadError(false);
    try {
      const configsData = await evoWebhookConfigService.getAll();
      if (requestId !== loadRequestRef.current) return;
      setConfigs(configsData);
      setFilteredConfigs(configsData);
    } catch (error) {
      if (requestId !== loadRequestRef.current) return;
      console.error('Error loading webhook configs:', error);
      setLoadError(true);
    } finally {
      if (requestId === loadRequestRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfigs();
  }, [loadConfigs]);

  // "Voltar" do overlay: página anterior do histórico, com fallback para a landing.
  const handleBack = () => {
    if (location.key !== 'default') {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  // Aplicar filtro
  useEffect(() => {
    if (!filter.trim()) {
      setFilteredConfigs(configs);
    } else {
      const filtered = configs.filter((config) => config.name.toLowerCase().includes(filter.toLowerCase()));
      setFilteredConfigs(filtered);
    }
    setPage(0); // Resetar página ao filtrar
  }, [filter, configs]);

  const handleOpenDialog = () => {
    setFormData({ name: '', notes: '' });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    if (submitting) return;
    setOpenDialog(false);
    setFormData({ name: '', notes: '' });
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setSnackbar({
        open: true,
        message: 'Nome é obrigatório',
        severity: 'error',
      });
      return;
    }

    try {
      setSubmitting(true);
      const result = await evoWebhookConfigService.create({
        name: formData.name.trim(),
        notes: formData.notes.trim() || undefined,
      });

      if (result.success && result.data) {
        setSnackbar({
          open: true,
          message: 'Cadastro criado com sucesso!',
          severity: 'success',
        });
        setOpenDialog(false);
        setFormData({ name: '', notes: '' });
        const configsData = await evoWebhookConfigService.getAll();
        setConfigs(configsData);
        navigate(`/evo-webhooks/${result.data.id}`);
      } else {
        throw new Error(result.error || 'Erro ao criar cadastro');
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error instanceof Error ? error.message : 'Erro ao criar cadastro',
        severity: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedConfigs = filteredConfigs.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  if (loading || loadError) {
    return <LoadingOverlay loading={loading} error={loadError} onRetry={loadConfigs} onBack={handleBack} />;
  }

  return (
    <Box p={3}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Webhooks EVO
        </Typography>
        <Tooltip title={canManage ? '' : 'Você não tem permissão de edição no módulo Webhooks EVO.'}>
          <span>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenDialog} disabled={!canManage}>
              Criar Novo Cadastro
            </Button>
          </span>
        </Tooltip>
      </Box>

      {/* Filtro */}
      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          label="Filtrar por nome"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          variant="outlined"
          size="small"
        />
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>Observações</TableCell>
              <TableCell>Credenciais</TableCell>
              <TableCell>Criado em</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedConfigs.length > 0 ? (
              paginatedConfigs.map((config) => (
                <TableRow key={config.id}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {config.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {config.notes || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {config.dns && config.token ? (
                      <Typography variant="body2" color="success.main">
                        Configuradas
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Não configuradas
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {config.createdAt?.seconds
                      ? new Date(config.createdAt.seconds * 1000).toLocaleDateString('pt-BR')
                      : '-'}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => navigate(`/evo-webhooks/${config.id}`)} color="primary" title="Ver detalhes">
                      <ViewIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="text.secondary">
                    {filter ? 'Nenhum cadastro encontrado com o filtro aplicado' : 'Nenhum cadastro encontrado'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filteredConfigs.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="Linhas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`}
        />
      </TableContainer>

      {/* Dialog para criar cadastro */}
      <Dialog
        open={openDialog}
        onClose={() => {
          if (submitting) return;
          handleCloseDialog();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Criar Novo Cadastro</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Nome"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              margin="normal"
              required
              disabled={submitting}
            />
            <TextField
              fullWidth
              label="Observações"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              margin="normal"
              multiline
              rows={4}
              disabled={submitting}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {submitting ? 'Criando…' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para notificações */}
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EvoWebhooksPage;

